/**
 * SpeakEngineModal — the presentation shell for a speak session.
 *
 * Purpose-agnostic: the capture / transcription / retry pipeline is fixed
 * and reusable, while the intro copy, organising prompt and preview UI are
 * driven by the preset the caller supplied to openCapture().
 *
 * Phases (identical for every preset):
 *   1. INTRO       — big mic button, per-preset copy + chips, recovery list
 *   2. RECORDING   — live waveform, timer, running transcript, Stop button
 *   3. STOPPING    — draining last chunks
 *   4. REVIEW      — full transcript + Retry-transcription banner if any
 *                    chunk failed, then "Organise with AI" CTA
 *   5. STRUCTURING — Claude Haiku working on the schema
 *   6. PREVIEW     — preset.PreviewComponent rendered with the structured
 *                    output. User confirms or edits, then the modal calls
 *                    session.onResult(finalValues) and closes.
 *   7. DONE        — short success ticker; auto-closes.
 *
 * ZERO-LOSS INVARIANT
 * -------------------
 *  - Every 12 s chunk is uploaded to Storage BEFORE being sent to the
 *    transcription API → browser crash cannot lose audio.
 *  - Every successful transcript segment is written to RTDB by the backend
 *    → the modal reads it live via onValue.
 *  - Failed segments keep their storagePath → the Retry banner re-invokes
 *    the transcription function without re-recording.
 *  - The user can close the modal at any post-recording phase; the memo
 *    is preserved and re-surfaces on the next open under "Unfinished
 *    recordings".
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ref as dbRef,
  onValue,
  set,
  update,
  remove,
} from 'firebase/database';
import { database } from '../firebaseConfig';
import { CLOUD_FUNCTION_URLS, authenticatedFetch } from '../utils/cloudFunctions';
import useVoiceMemoRecorder from './useVoiceMemoRecorder';
import './SpeakEngineModal.css';

// Statuses that mean "user did NOT finish the process" — we surface them on
// the intro screen so nothing gets orphaned after a crash / accidental close.
const INCOMPLETE_STATUSES = new Set([
  'recording',
  'awaiting-transcription',
  'transcribed',
  'structure-failed',
  'structuring',
]);

function formatDuration(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatRelative(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

function segmentsToTranscript(segmentsObj) {
  if (!segmentsObj) return { text: '', items: [], failed: 0 };
  const items = Object.entries(segmentsObj)
    .map(([k, v]) => ({ idx: Number(k), ...(v || {}) }))
    .sort((a, b) => a.idx - b.idx);
  // Take the text of every segment that has one. Keying this off
  // `status === 'done'` dropped real transcript text whenever a segment
  // arrived with a different status ('empty', a retry's 'pending', or none
  // at all), which looked to the user like the recording produced nothing.
  const text = items
    .map((s) => (s.status !== 'failed' && typeof s.text === 'string' ? s.text.trim() : ''))
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
  const failed = items.filter((s) => s.status === 'failed').length;
  return { text, items, failed };
}

/**
 * Props:
 *   session: { preset, context, onResult, onCancel } — from SpeakEngineProvider
 *   userId, currentUserId, canEdit
 *   onClose: () => void
 */
export default function SpeakEngineModal({
  session, userId, currentUserId, canEdit, onClose,
}) {
  const { preset, context } = session;
  const rec = useVoiceMemoRecorder({ userId, currentUserId, canEdit });
  const [segments, setSegments] = useState({});
  const [memoDoc, setMemoDoc] = useState(null);
  const [phase, setPhase] = useState('intro');
  const [recentIncomplete, setRecentIncomplete] = useState([]);
  const [selectedMemoId, setSelectedMemoId] = useState(null);
  const [aiError, setAiError] = useState('');
  const [structured, setStructured] = useState(null);
  const [confirmingResult, setConfirmingResult] = useState(false);
  const [confirmedSummary, setConfirmedSummary] = useState('');
  const [retryingChunks, setRetryingChunks] = useState(() => new Set());
  const [retryError, setRetryError] = useState('');
  const transcriptScrollRef = useRef(null);
  const autoCloseTimerRef = useRef(null);

  const activeMemoId = selectedMemoId || rec.memoId;

  // ─── Live subscription to the active memo doc ─────────────────────────────
  useEffect(() => {
    if (!activeMemoId) {
      setSegments({});
      setMemoDoc(null);
      return undefined;
    }
    const memoRef = dbRef(database, `World/${userId}/dataVault/voiceMemos/${activeMemoId}`);
    const unsub = onValue(memoRef, (snap) => {
      const val = snap.val();
      setMemoDoc(val || null);
      setSegments(val?.segments || {});
    });
    return () => unsub();
  }, [activeMemoId, userId]);

  // ─── Recovery list on mount ───────────────────────────────────────────────
  useEffect(() => {
    if (!userId) return undefined;
    const memosRef = dbRef(database, `World/${userId}/dataVault/voiceMemos`);
    const unsub = onValue(memosRef, (snap) => {
      const val = snap.val() || {};
      const now = Date.now();
      const list = Object.entries(val)
        .map(([id, m]) => ({ id, ...(m || {}) }))
        .filter((m) => {
          if (!INCOMPLETE_STATUSES.has(m.status)) return false;
          const t = m.stoppedAt || m.updatedAt || m.createdAt;
          if (!t) return true;
          return (now - new Date(t).getTime()) < 24 * 60 * 60 * 1000;
        })
        .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0))
        .slice(0, 4);
      setRecentIncomplete(list);
    });
    return () => unsub();
  }, [userId]);

  // ─── Phase machine from recorder state ────────────────────────────────────
  // Stay on intro while the browser permission prompt is open. Jumping to
  // the recording UI here is what made first-time mic grant look hung.
  useEffect(() => {
    if (rec.state === 'requesting') setPhase('intro');
    else if (rec.state === 'recording') setPhase('recording');
    else if (rec.state === 'stopping') setPhase('stopping');
    else if (rec.state === 'stopped') setPhase('review');
    else if (rec.state === 'cancelled' || rec.state === 'error') setPhase('intro');
  }, [rec.state]);

  // ─── Phase machine from memoDoc status ────────────────────────────────────
  useEffect(() => {
    if (!memoDoc) return;
    if (memoDoc.status === 'structured' && memoDoc.structured) {
      // Re-opening a memo that was already structured on a previous visit.
      if (phase !== 'preview' && phase !== 'done' && phase !== 'intro') {
        setStructured(memoDoc.structured);
        setPhase('preview');
      }
    } else if (memoDoc.status === 'structure-failed' && phase === 'structuring') {
      setAiError(memoDoc.structureError || 'Structuring failed. Please try again.');
      setPhase('review');
    } else if (memoDoc.status === 'structuring' && phase !== 'structuring') {
      setPhase('structuring');
    }
  }, [memoDoc, phase]);

  // ─── Autoscroll live transcript ───────────────────────────────────────────
  useEffect(() => {
    if (!transcriptScrollRef.current) return;
    transcriptScrollRef.current.scrollTop = transcriptScrollRef.current.scrollHeight;
  }, [segments]);

  const derivedTranscript = useMemo(() => segmentsToTranscript(segments), [segments]);

  // "Still working" must never get stuck on, or it hides the real state of
  // the transcript. Only an explicitly pending segment counts, and the chunk
  // counters only mean "in flight" while the recorder itself is still running
  // — after stop() every chunk has resolved one way or the other.
  const isTranscribing = useMemo(() => {
    if (derivedTranscript.items.some((s) => s.status === 'pending')) return true;
    if (rec.state !== 'recording' && rec.state !== 'stopping') return false;
    return rec.chunkStats.total > rec.chunkStats.transcribed + rec.chunkStats.failed;
  }, [derivedTranscript.items, rec.state, rec.chunkStats]);

  // ─── ESC key ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (phase === 'recording' || phase === 'stopping' || phase === 'structuring' || confirmingResult) return;
      onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, confirmingResult, onClose]);

  // ─── Actions ─────────────────────────────────────────────────────────────
  const handleStart = useCallback(async () => {
    setAiError('');
    setStructured(null);
    setSelectedMemoId(null);
    await rec.start({});
  }, [rec]);

  const handleStop = useCallback(async () => { await rec.stop(); }, [rec]);
  const handleCancelRecording = useCallback(async () => { await rec.cancel(); setPhase('intro'); }, [rec]);

  const handleContinueMemo = useCallback((memo) => {
    setAiError('');
    setSelectedMemoId(memo.id);
    if (memo.status === 'structuring') setPhase('structuring');
    else if (memo.status === 'structured' && memo.structured) {
      setStructured(memo.structured);
      setPhase('preview');
    } else setPhase('review');
  }, []);

  const handleDiscardIncomplete = useCallback(async (memoId) => {
    if (!memoId) return;
    try {
      await update(dbRef(database, `World/${userId}/dataVault/voiceMemos/${memoId}`), {
        status: 'cancelled',
        cancelledAt: new Date().toISOString(),
      });
    } catch (_) { /* noop */ }
  }, [userId]);

  const handleOrganize = useCallback(async () => {
    if (!activeMemoId) return;
    setAiError('');
    setPhase('structuring');
    try {
      const resp = await authenticatedFetch(CLOUD_FUNCTION_URLS.structureVoiceMemoAny(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          memoId: activeMemoId,
          presetId: preset.id,
          schema: preset.schema,
          context: context || {},
        }),
      });
      if (!resp.ok) {
        const body = await resp.text().catch(() => '');
        throw new Error(`HTTP ${resp.status}: ${body.slice(0, 220)}`);
      }
      const data = await resp.json();
      if (data?.empty) {
        setAiError('There was nothing to organise. The transcript is empty.');
        setPhase('review');
        return;
      }
      setStructured(data?.structured || null);
      setPhase('preview');
    } catch (e) {
      setAiError(e.message || 'AI organising failed. Try again.');
      setPhase('review');
    }
  }, [activeMemoId, userId, preset, context]);

  const handleConfirmResult = useCallback(async (finalValues) => {
    if (!session?.onResult) {
      onClose?.();
      return;
    }
    setConfirmingResult(true);
    try {
      // Caller decides what happens with the values. May be async — we await
      // so the "Applying…" button state accurately reflects DB writes.
      const summary = await session.onResult(finalValues, { memoId: activeMemoId, structured, preset });
      setConfirmedSummary(typeof summary === 'string' ? summary : '');
      // Mark the memo as consumed so it stops appearing in recovery lists.
      if (activeMemoId) {
        try {
          await update(dbRef(database, `World/${userId}/dataVault/voiceMemos/${activeMemoId}`), {
            status: 'imported',
            importedAt: new Date().toISOString(),
            importedIntoPreset: preset.id,
          });
        } catch (_) { /* best-effort */ }
      }
      setPhase('done');
      autoCloseTimerRef.current = setTimeout(() => onClose?.(), 1800);
    } catch (e) {
      setAiError(e?.message || 'Could not apply the result.');
    } finally {
      setConfirmingResult(false);
    }
  }, [session, activeMemoId, structured, preset, userId, onClose]);

  useEffect(() => () => {
    if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
  }, []);

  const handleDeleteMemo = useCallback(async () => {
    if (!activeMemoId) return;
    try {
      await remove(dbRef(database, `World/${userId}/dataVault/voiceMemos/${activeMemoId}`));
    } catch (_) { /* noop */ }
    rec.reset();
    setSelectedMemoId(null);
    setStructured(null);
    setPhase('intro');
  }, [activeMemoId, userId, rec]);

  const handleRetryFailed = useCallback(async () => {
    if (!activeMemoId) return;
    const failed = derivedTranscript.items.filter((s) => s.status === 'failed' && s.storagePath);
    if (failed.length === 0) return;
    setRetryError('');
    setRetryingChunks(new Set(failed.map((s) => s.idx)));

    let lastError = '';
    for (const seg of failed) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await update(
          dbRef(database, `World/${userId}/dataVault/voiceMemos/${activeMemoId}/segments/${seg.idx}`),
          { status: 'pending', retryStartedAt: new Date().toISOString(), error: null }
        );
        // eslint-disable-next-line no-await-in-loop
        const resp = await authenticatedFetch(CLOUD_FUNCTION_URLS.transcribeVoiceMemoChunk(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId,
            memoId: activeMemoId,
            chunkIndex: seg.idx,
            storagePath: seg.storagePath,
            durationMs: seg.durationMs || 0,
            mimeType: /\.m4a$/i.test(seg.storagePath) ? 'audio/mp4'
              : /\.ogg$/i.test(seg.storagePath) ? 'audio/ogg'
              : 'audio/webm',
          }),
        });
        if (!resp.ok) {
          // eslint-disable-next-line no-await-in-loop
          const body = await resp.text().catch(() => '');
          throw new Error(`HTTP ${resp.status}: ${body.slice(0, 220)}`);
        }
      } catch (e) {
        lastError = e.message || 'Retry failed';
      } finally {
        setRetryingChunks((prev) => {
          const next = new Set(prev);
          next.delete(seg.idx);
          return next;
        });
      }
    }
    if (lastError) setRetryError(lastError);
  }, [activeMemoId, userId, derivedTranscript.items]);

  // ─── Render ───────────────────────────────────────────────────────────────
  const canClose = phase !== 'recording' && phase !== 'stopping' && phase !== 'structuring' && !confirmingResult;

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="speakEngine__backdrop"
      onClick={(e) => { if (e.target === e.currentTarget && canClose) onClose?.(); }}
      role="dialog"
      aria-modal="true"
      aria-label={preset.title}
    >
      <div className="speakEngine__card">
        <header className="speakEngine__head">
          <div className="speakEngine__headTitle">
            <h2>{preset.title}</h2>
            {preset.subtitle && <p>{preset.subtitle}</p>}
          </div>
          <button type="button" className="speakEngine__closeBtn" onClick={() => canClose && onClose?.()} disabled={!canClose} aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </header>

        {phase === 'intro' && (
          <IntroPhase
            preset={preset}
            canEdit={canEdit}
            error={rec.error}
            requesting={rec.state === 'requesting'}
            onStart={handleStart}
            onCancelRequest={handleCancelRecording}
            recentIncomplete={recentIncomplete}
            onContinue={handleContinueMemo}
            onDiscard={handleDiscardIncomplete}
          />
        )}

        {(phase === 'recording' || phase === 'stopping') && (
          <RecordingPhase
            preset={preset}
            phase={phase}
            elapsedMs={rec.elapsedMs}
            level={rec.level}
            transcriptItems={derivedTranscript.items}
            transcriptText={derivedTranscript.text}
            chunkStats={rec.chunkStats}
            isTranscribing={isTranscribing}
            transcriptScrollRef={transcriptScrollRef}
            onStop={handleStop}
            onCancel={handleCancelRecording}
            maxMemoMs={rec.maxMemoMs}
          />
        )}

        {phase === 'review' && (
          <ReviewPhase
            preset={preset}
            transcriptText={derivedTranscript.text}
            transcriptItems={derivedTranscript.items}
            memoDoc={memoDoc}
            aiError={aiError}
            onOrganize={handleOrganize}
            onDelete={handleDeleteMemo}
            onRerecord={() => { rec.reset(); setSelectedMemoId(null); setStructured(null); setPhase('intro'); }}
            retryingChunks={retryingChunks}
            retryError={retryError}
            onRetryFailed={handleRetryFailed}
            isTranscribing={isTranscribing}
          />
        )}

        {phase === 'structuring' && (
          <StructuringPhase preset={preset} />
        )}

        {phase === 'preview' && structured && (
          <preset.PreviewComponent
            structured={structured}
            context={context}
            saving={confirmingResult}
            onConfirm={handleConfirmResult}
            onCancel={() => setPhase('review')}
          />
        )}

        {phase === 'done' && (
          <DonePhase preset={preset} summary={confirmedSummary} onClose={() => onClose?.()} />
        )}
      </div>
    </div>,
    document.body
  );
}

// ─── Phase components ────────────────────────────────────────────────────────

function IntroPhase({ preset, canEdit, error, requesting, onStart, onCancelRequest, recentIncomplete, onContinue, onDiscard }) {
  return (
    <div className="speakEngine__body speakEngine__body--intro">
      <div className="speakEngine__introHero">
        <button
          type="button"
          className={`speakEngine__micButton ${requesting ? 'is-requesting' : 'speakEngine__micButton--idle'}`}
          onClick={onStart}
          disabled={!canEdit || requesting}
          aria-label={requesting ? 'Waiting for microphone permission' : 'Start recording'}
        >
          <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <rect x="9" y="2" width="6" height="12" rx="3" />
            <path d="M5 10a7 7 0 0 0 14 0" />
            <path d="M12 19v3" />
          </svg>
        </button>
        <h3>{requesting ? 'Waiting for microphone' : (preset.intro?.heading || 'Tap to start speaking')}</h3>
        <p className="speakEngine__introHint">
          {requesting
            ? 'Your browser will ask for microphone access. Allow it in the prompt — recording starts right after.'
            : preset.intro?.hint}
        </p>
        {!requesting && preset.intro?.chips?.length > 0 && (
          <div className="speakEngine__introChips">
            {preset.intro.chips.map((c) => (
              <span key={c} className="speakEngine__introChip">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
                  <path d="M20 6 9 17l-5-5" />
                </svg>
                {c}
              </span>
            ))}
          </div>
        )}

        {requesting ? (
          <button
            type="button"
            className="speakEngine__btn speakEngine__btn--ghost"
            onClick={onCancelRequest}
          >
            Cancel
          </button>
        ) : (
          <button
            type="button"
            className="speakEngine__recordCta"
            onClick={onStart}
            disabled={!canEdit}
          >
            <span className="speakEngine__recordCtaDot" aria-hidden />
            Record now
          </button>
        )}

        {!canEdit && (
          <div className="speakEngine__err">You have read-only access to this account, so voice memos are disabled.</div>
        )}
        {error && <div className="speakEngine__err">{error}</div>}
      </div>

      {(preset.intro?.questions?.length > 0 || recentIncomplete.length > 0) && (
        <div className="speakEngine__introDock">
          {preset.intro?.questions?.length > 0 && (
            <details className="speakEngine__introQuestions">
              <summary className="speakEngine__introQuestionsToggle">
                <span className="speakEngine__introQuestionsToggleText">
                  {preset.intro.questionsHeading || 'Guiding questions'}
                  <span className="speakEngine__introQuestionsCount">{preset.intro.questions.length}</span>
                </span>
                <svg
                  className="speakEngine__introQuestionsChevron"
                  width="14" height="14" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="2.2"
                  strokeLinecap="round" strokeLinejoin="round" aria-hidden
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </summary>
              <ol className="speakEngine__introQuestionsList">
                {preset.intro.questions.map((q, i) => (
                  <li key={i} className="speakEngine__introQuestionItem">
                    <span className="speakEngine__introQuestionNum">{i + 1}</span>
                    <span className="speakEngine__introQuestionText">{q}</span>
                  </li>
                ))}
              </ol>
            </details>
          )}

          {recentIncomplete.length > 0 && (
            <details className="speakEngine__recent">
              <summary className="speakEngine__recentToggle">
                <span className="speakEngine__recentToggleText">
                  Unfinished
                  <span className="speakEngine__recentCount">{recentIncomplete.length}</span>
                </span>
                <svg
                  className="speakEngine__recentChevron"
                  width="14" height="14" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="2.2"
                  strokeLinecap="round" strokeLinejoin="round" aria-hidden
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </summary>
              <ul className="speakEngine__recentList">
                {recentIncomplete.map((m) => {
                  const segCount = m.segments ? Object.keys(m.segments).length : 0;
                  return (
                    <li key={m.id} className="speakEngine__recentItem">
                      <div className="speakEngine__recentMain">
                        <span className="speakEngine__recentTitle">
                          {m.status === 'recording' ? 'Interrupted recording' : 'Ready to organise'}
                        </span>
                        <span className="speakEngine__recentMeta">
                          {segCount} {segCount === 1 ? 'chunk' : 'chunks'} · {formatRelative(m.updatedAt || m.createdAt)}
                        </span>
                      </div>
                      <button type="button" className="speakEngine__recentContinue" onClick={() => onContinue(m)}>Continue</button>
                      <button
                        type="button"
                        className="speakEngine__recentDiscard"
                        onClick={() => onDiscard(m.id)}
                        title="Discard this recording"
                        aria-label="Discard"
                      >
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
                          <path d="M18 6 6 18M6 6l12 12" />
                        </svg>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function RecordingPhase({
  preset, phase, elapsedMs, level, transcriptItems, transcriptText, chunkStats,
  isTranscribing, transcriptScrollRef, onStop, onCancel, maxMemoMs,
}) {
  const isStopping = phase === 'stopping';
  const questions = preset?.intro?.questions || [];
  const pctOfMax = Math.min(100, (elapsedMs / maxMemoMs) * 100);
  return (
    <div className="speakEngine__body speakEngine__body--recording">
      <div className="speakEngine__meterWrap">
        <div className={`speakEngine__pulseRing ${isStopping ? 'is-stopping' : ''}`} aria-hidden>
          <div className="speakEngine__pulseCore" style={{ transform: `scale(${1 + level * 0.6})` }} />
        </div>
        <div className="speakEngine__meterInfo">
          <span className="speakEngine__timer">{formatDuration(elapsedMs)}</span>
          <span className={`speakEngine__statusLine ${isStopping ? 'is-stopping' : ''}`}>
            {isStopping ? 'Wrapping up, uploading the last chunk…' : 'Recording · every ~12 s is autosaved'}
          </span>
        </div>
      </div>

      <div className="speakEngine__progressTrack" aria-hidden>
        <div className="speakEngine__progressFill" style={{ width: `${pctOfMax}%` }} />
      </div>

      {/* The guiding prompts are what the user is actually talking through,
          so they have to stay on screen while recording — not only on the
          intro card they just left. */}
      {questions.length > 0 && (
        <details className="speakEngine__recQuestions" open>
          <summary className="speakEngine__recQuestionsToggle">
            <span className="speakEngine__recQuestionsToggleText">
              {preset.intro.questionsHeading || 'Guiding questions'}
              <span className="speakEngine__recQuestionsCount">{questions.length}</span>
            </span>
            <svg
              className="speakEngine__recQuestionsChevron"
              width="14" height="14" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2.2"
              strokeLinecap="round" strokeLinejoin="round" aria-hidden
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </summary>
          <ol className="speakEngine__recQuestionsList">
            {questions.map((q, i) => (
              <li key={q}>
                <span className="speakEngine__recQuestionsNum" aria-hidden>{i + 1}</span>
                <span>{q}</span>
              </li>
            ))}
          </ol>
        </details>
      )}

      <div className="speakEngine__transcriptShell">
        <div className="speakEngine__transcriptHead">
          <span className="speakEngine__transcriptLabel">Live transcript</span>
          <span className="speakEngine__transcriptStat">
            {isTranscribing && (
              <span className="speakEngine__transcribingPill">
                <span className="speakEngine__btnSpinner speakEngine__btnSpinner--ink" aria-hidden />
                Transcribing…
              </span>
            )}
            {chunkStats.transcribed}/{chunkStats.total || '·'} chunks
            {chunkStats.failed > 0 && <span className="speakEngine__transcriptFail"> · {chunkStats.failed} failed</span>}
          </span>
        </div>
        <div className="speakEngine__transcriptBody" ref={transcriptScrollRef}>
          {transcriptText ? (
            <p className="speakEngine__transcriptText">
              {transcriptText}
              {isTranscribing && <span className="speakEngine__transcriptCursor" aria-hidden />}
            </p>
          ) : (
            <p className="speakEngine__transcriptEmpty">
              {isStopping ? 'Finalising last chunks…' : 'Words will appear here as soon as the first chunk is transcribed (usually within 15 s).'}
            </p>
          )}
          {transcriptItems.some((s) => s.status === 'failed') && (
            <div className="speakEngine__transcriptFailNote">
              Some chunks failed to transcribe. The raw audio is still saved, so you can retry from the review screen.
            </div>
          )}
        </div>
      </div>

      <div className="speakEngine__recordActions">
        <button type="button" className="speakEngine__btn speakEngine__btn--ghost" onClick={onCancel} disabled={isStopping}>
          Cancel
        </button>
        <button type="button" className="speakEngine__btn speakEngine__btn--primary speakEngine__btn--stop" onClick={onStop} disabled={isStopping}>
          {isStopping ? (
            <><span className="speakEngine__btnSpinner" aria-hidden /> Wrapping up…</>
          ) : (
            <>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <rect x="6" y="6" width="12" height="12" rx="1.5" />
              </svg>
              Stop &amp; organise
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function ReviewPhase({
  preset, transcriptText, transcriptItems, memoDoc, aiError, onOrganize, onDelete, onRerecord,
  retryingChunks, retryError, onRetryFailed, isTranscribing,
}) {
  const failedItems = transcriptItems.filter((s) => s.status === 'failed');
  const failedRetryable = failedItems.filter((s) => !!s.storagePath);
  const failedCount = failedItems.length;
  const emptyCount = transcriptItems.filter((s) => s.status === 'empty').length;
  const hasText = !!transcriptText;
  const isRetrying = retryingChunks && retryingChunks.size > 0;
  const doneCount = transcriptItems.filter((s) => s.status === 'done').length;
  const errorMessage = retryError || (failedItems.find((s) => s.error)?.error) || '';
  const isCreditIssue = /insufficient_quota|out of credits?|insufficient balance|billing|payment required|402|429/i.test(errorMessage);

  return (
    <div className="speakEngine__body speakEngine__body--review">
      <div className="speakEngine__reviewHead">
        <h3>{hasText ? 'Transcript ready' : 'Recording captured'}</h3>
        <p>
          {failedCount > 0 && !hasText
            ? 'Your audio is safely saved. The transcription step failed. You can retry it below without re-recording.'
            : failedCount > 0
              ? `${doneCount} of ${transcriptItems.length} chunks transcribed. Retry the rest below, then continue.`
              : 'Review the raw transcript below, then let AI structure it.'}
        </p>
      </div>

      {failedCount > 0 && (
        <div className="speakEngine__retryBanner">
          <div className="speakEngine__retryBannerHead">
            <div className="speakEngine__retryBannerIcon" aria-hidden>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" x2="12" y1="8" y2="12" />
                <line x1="12" x2="12.01" y1="16" y2="16" />
              </svg>
            </div>
            <div className="speakEngine__retryBannerText">
              <strong>{failedCount} {failedCount === 1 ? 'chunk' : 'chunks'} couldn't be transcribed</strong>
              <span>
                Your audio is safe in cloud storage. Nothing has been lost. You can retry the transcription any time, also later once credits are topped up.
              </span>
              {errorMessage && (
                <span className="speakEngine__retryBannerReason">
                  <span className="speakEngine__retryBannerReasonLabel">Reason:</span> {errorMessage.slice(0, 320)}
                  {isCreditIssue && (
                    <span className="speakEngine__retryBannerHint">
                      Looks like the OpenAI account is out of credits. Top up billing (or configure the Groq / Deepgram fallback key) and hit retry.
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>
          {failedRetryable.length > 0 && (
            <button
              type="button"
              className="speakEngine__btn speakEngine__btn--primary speakEngine__retryBannerBtn"
              onClick={onRetryFailed}
              disabled={isRetrying}
            >
              {isRetrying ? (
                <><span className="speakEngine__btnSpinner" aria-hidden /> Retrying {retryingChunks.size} {retryingChunks.size === 1 ? 'chunk' : 'chunks'}…</>
              ) : (
                <>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M3 12a9 9 0 0 1 15.5-6.36L21 8" />
                    <path d="M21 3v5h-5" />
                    <path d="M21 12a9 9 0 0 1-15.5 6.36L3 16" />
                    <path d="M3 21v-5h5" />
                  </svg>
                  Retry transcription
                </>
              )}
            </button>
          )}
        </div>
      )}

      <div className="speakEngine__transcriptShell speakEngine__transcriptShell--full">
        <div className="speakEngine__transcriptHead">
          <span className="speakEngine__transcriptLabel">Transcript</span>
          <span className="speakEngine__transcriptStat">
            {(isTranscribing || isRetrying) && (
              <span className="speakEngine__transcribingPill">
                <span className="speakEngine__btnSpinner speakEngine__btnSpinner--ink" aria-hidden />
                Transcribing…
              </span>
            )}
            {transcriptItems.length} {transcriptItems.length === 1 ? 'chunk' : 'chunks'}
            {failedCount > 0 && <span className="speakEngine__transcriptFail"> · {failedCount} failed</span>}
            {memoDoc?.totalElapsedMs > 0 && <> · {formatDuration(memoDoc.totalElapsedMs)}</>}
          </span>
        </div>
        <div className="speakEngine__transcriptBody speakEngine__transcriptBody--full">
          {hasText ? (
            <p className="speakEngine__transcriptText">
              {transcriptText}
              {(isRetrying || isTranscribing) && <span className="speakEngine__transcriptCursor" aria-hidden />}
            </p>
          ) : failedCount > 0 ? (
            <p className="speakEngine__transcriptEmpty">No text captured yet. Use “Retry transcription” above. Your audio is safe.</p>
          ) : emptyCount > 0 ? (
            <p className="speakEngine__transcriptEmpty">
              The recording came back silent — no speech was detected in {emptyCount === transcriptItems.length ? 'any chunk' : `${emptyCount} of ${transcriptItems.length} chunks`}.
              Check that the right microphone is selected in your browser, then record again.
            </p>
          ) : isRetrying || isTranscribing ? (
            <p className="speakEngine__transcriptEmpty">Still transcribing the last chunks — the text appears as they come back.</p>
          ) : (
            <p className="speakEngine__transcriptEmpty">No transcript captured yet. If chunks are still processing, wait a moment and try again.</p>
          )}
        </div>
      </div>
      {aiError && <div className="speakEngine__err">{aiError}</div>}
      <div className="speakEngine__reviewActions">
        <button type="button" className="speakEngine__btn speakEngine__btn--ghost" onClick={onRerecord}>Record again</button>
        <button type="button" className="speakEngine__btn speakEngine__btn--ghost" onClick={onDelete}>Discard</button>
        <button
          type="button"
          className="speakEngine__btn speakEngine__btn--primary"
          onClick={onOrganize}
          disabled={!hasText || isRetrying}
          title={!hasText ? 'Retry transcription first so there is text to organise' : ''}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z" />
            <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z" />
          </svg>
          {preset.organizeCta || 'Organise with AI'}
        </button>
      </div>
    </div>
  );
}

function StructuringPhase({ preset }) {
  return (
    <div className="speakEngine__body speakEngine__body--structuring">
      <div className="speakEngine__structuringSpinner" aria-hidden>
        <div /><div /><div /><div />
      </div>
      <h3>Organising your notes…</h3>
      <p>{preset.organizingText || 'AI is structuring your input. Usually 3–8 seconds.'}</p>
    </div>
  );
}

function DonePhase({ preset, summary, onClose }) {
  const label = summary || (typeof preset.doneText === 'function' ? preset.doneText(1) : (preset.doneText || 'Done'));
  return (
    <div className="speakEngine__body speakEngine__body--done">
      <div className="speakEngine__doneIcon" aria-hidden>
        <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 6 9 17l-5-5" />
        </svg>
      </div>
      <h3>{label}</h3>
      <p>Auto-closing in a moment.</p>
      <button type="button" className="speakEngine__btn speakEngine__btn--primary" onClick={onClose}>Done</button>
    </div>
  );
}
